import React, { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  TextField,
  Button,
} from '@finpay-development/shared-components';
import {
  Box,
  Grid,
  Paper,
  Typography,
  MenuItem,
} from '@mui/material';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from '../../shared/state/store';
import { getClientFacilities } from '../../implementation-specialist/state/facility/implementation-facility-thunk';
import { addPatientToRecentPatientsIfNeeded } from '../../patient/state/patient-thunk';
import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { patientService } from '../../patient/services/patient-service';
import { showErrorStatus } from '../../security/state/user-slice';
import { ImplementationFacility } from '../../implementation-specialist/components/implementation-clients/details/models/implementation-facility';
import OpenSearchPatient
  from '../../patient/components/patient-overview/open-search-patient';
import {patientSearchResult} from '../../admissions-advisor/models/patient';

interface HubFormValues {
  patientName: string;
  facility: string;
  ioc: string;
}

const validationSchema = Yup.object({
  patientName: Yup.string().required('Please select a patient'),
  facility: Yup.string().optional(), // Hidden for now
  ioc: Yup.string().required('Please select an IOC'),
});

function HubView() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ patientId?: string; patientEncounterId?: string }>();
  const patientIdParam = params.patientId;
  const patientEncounterIdParam = params.patientEncounterId;
  const dispatch = useDispatch<AppDispatch>();

  // Parse patientId and encounterId from URL path - useParams can be unreliable in some route setups
  const urlIds = (() => {
    const match = location.pathname.match(/\/hub\/patient\/(\d+)(?:\/ioc\/(\d+))?/);
    if (match) {
      return {
        patientId: parseInt(match[1], 10),
        encounterId: match[2] ? parseInt(match[2], 10) : undefined,
      };
    }
    return null;
  })();

  const [patients, setPatients] = useState<any[]>([]);
  const [iocDates, setIocDates] = useState<{ id: number; date: string; display: string }[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isLoadingIocs, setIsLoadingIocs] = useState(false);

  const facilities = useSelector((state: RootState) => 
    state.implementationContext.implementationFacility.facilities
  ).flat() as ImplementationFacility[];

  const allowedClient = useSelector((state: RootState) => 
    state.userContext.userProfile.allowedClients[0]
  );

  const recentPatients = useSelector((state: RootState) => 
    state.patientContext.recentPatients
  );

  const userId = useSelector((state: RootState) => 
    state.userContext.userProfile.userId
  );

  const selectedEncounter = useSelector((state: RootState) => state.patientContext.selectedEncounter);
  const selectedPatient = useSelector((state: RootState) => state.patientContext.selectedPatient);

  const getPatientAndEncounterIds = (
    patientIdFromForm?: string
  ): { patientId: number; encounterId?: number } | null => {
    // Prioritize URL - parse from pathname directly (reliable when useParams has issues)
    if (urlIds && urlIds.patientId) {
      return {
        patientId: urlIds.patientId,
        encounterId: urlIds.encounterId,
      };
    }
    // Fallback to useParams
    if (patientIdParam && patientEncounterIdParam) {
      const pid = parseInt(patientIdParam, 10);
      const eid = parseInt(patientEncounterIdParam, 10);
      if (!isNaN(pid) && !isNaN(eid)) return { patientId: pid, encounterId: eid };
    }
    if (patientIdParam) {
      const pid = parseInt(patientIdParam, 10);
      if (!isNaN(pid)) return { patientId: pid };
    }
    if (selectedEncounter?.patientId && selectedEncounter?.patientEncounterId) {
      return {
        patientId: selectedEncounter.patientId,
        encounterId: selectedEncounter.patientEncounterId,
      };
    }
    if (selectedPatient?.patientId) {
      const firstEncounter = selectedPatient.patientEncounters?.[0];
      if (firstEncounter?.patientEncounterId) {
        return {
          patientId: selectedPatient.patientId,
          encounterId: firstEncounter.patientEncounterId,
        };
      }
      return { patientId: selectedPatient.patientId };
    }
    if (patientIdFromForm) {
      const pid = parseInt(patientIdFromForm, 10);
      if (!isNaN(pid)) return { patientId: pid };
    }
    // Fallback: use first patient from recent patients list if not empty
    const firstRecentPatient = recentPatients.find(
      (p) => p.patientId && p.patientName
    );
    if (firstRecentPatient) {
      return { patientId: firstRecentPatient.patientId };
    }
    return null;
  };

  useEffect(() => {
    if (allowedClient?.clientId) {
      dispatch(getClientFacilities(allowedClient.clientId));
    }
  }, [dispatch, allowedClient]);

  // Add patient to recent patients if in URL and not already in list
  useEffect(() => {
    if (patientIdParam && userId && userId > 0) {
      const pid = parseInt(patientIdParam, 10);
      if (!isNaN(pid) && !recentPatients.some((p) => p.patientId === pid)) {
        dispatch(addPatientToRecentPatientsIfNeeded(pid));
      }
    }
  }, [dispatch, patientIdParam, userId, recentPatients]);

  // Get first valid patient from Redux recent patients
  const getFirstValidPatient = (): string => {
    const validPatient = recentPatients.find(
      (patient) => patient.patientId && patient.patientName
    );
    return validPatient ? validPatient.patientId.toString() : '';
  };

  const initialValues: HubFormValues = useMemo(() => {
    const patientIdFromUrl = patientIdParam ?? '';
    const firstPatientId = patientIdFromUrl || getFirstValidPatient();
    const iocFromUrl = patientEncounterIdParam ?? '';
    return {
      patientName: firstPatientId,
      facility: '',
      ioc: iocFromUrl,
    };
  }, [patientIdParam, patientEncounterIdParam, recentPatients]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Handle form submission
      console.log('Form submitted:', values);
    },
  });

  // Load IOCs when component mounts, patientId from URL changes, recent patients load, or user selects different patient from dropdown
  useEffect(() => {
    const patientId = formik.values.patientName || patientIdParam || getFirstValidPatient();
    if (patientId) {
      loadIocs(patientId, '');
    }
  }, [patientIdParam, recentPatients, formik.values.patientName]);


  const handleNewPatient = () => {
    // Navigate to new patient creation
    navigate('/hub/patient');
  };

  const handleFacilityChange = async (facilityId: string) => {
    formik.setFieldValue('facility', facilityId, false);
    formik.setFieldValue('ioc', '', false);
    setIocDates([]);

    if (facilityId && formik.values.patientName) {
      // Load IOCs for selected patient and facility
      await loadIocs(formik.values.patientName, facilityId);
    }
  };

  const handlePatientChange = async (patientId: string) => {
    formik.setFieldValue('patientName', patientId, false);
    formik.setFieldValue('ioc', '', false);
    setIocDates([]);

    if (patientId) {
      // Load IOCs for selected patient (facility hidden for now)
      await loadIocs(patientId, '');
    }
  };

  const loadIocs = async (patientId: string, _facilityId: string) => {
    if (!patientId) return;

    const pid = parseInt(patientId, 10);
    if (isNaN(pid)) return;

    setIsLoadingIocs(true);
    try {
      const response = await patientService.getPatientInstancesOfCare({
        patientId: pid,
        limit: 100,
        offset: 0,
      });

      if (response.hasErrors || !response.entity) {
        setIocDates([]);
        formik.setFieldValue('ioc', '', false);
        return;
      }

      const encounters = (Array.isArray(response.entity) ? response.entity : []) as PatientEncounter[];
      const iocList = encounters.map((enc: PatientEncounter) => {
        const date = enc.instanceCreateDt
          ? new Date(enc.instanceCreateDt).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : '';
        const facility = enc.facilityName || '';
        const display = [enc.clientsPatientIOCId || enc.patientEncounterId, facility, date].filter(Boolean).join(' - ');
        return {
          id: enc.patientEncounterId,
          date: enc.instanceCreateDt || '',
          display: display || `IOC ${enc.patientEncounterId}`,
        };
      });

      setIocDates(iocList);

      // Pre-select: URL encounterId, or first IOC
      const encounterIdFromUrl = urlIds?.encounterId ?? (patientEncounterIdParam ? parseInt(patientEncounterIdParam, 10) : undefined);
      const selectedId = encounterIdFromUrl && iocList.some((i) => i.id === encounterIdFromUrl)
        ? encounterIdFromUrl.toString()
        : (iocList[0]?.id?.toString() ?? '');
      formik.setFieldValue('ioc', selectedId, false);

      // Keep URL in sync when patient or IOC differs from current URL
      if (iocList.length > 0 && selectedId) {
        const urlNeedsUpdate = !urlIds || urlIds.patientId !== pid || urlIds.encounterId !== parseInt(selectedId, 10);
        if (urlNeedsUpdate) {
          navigate(`/hub/patient/${pid}/ioc/${selectedId}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error loading IOCs:', error);
      setIocDates([]);
      formik.setFieldValue('ioc', '', false);
    } finally {
      setIsLoadingIocs(false);
    }
  };

  const handleTakePayment = () => {
    const ids = getPatientAndEncounterIds(formik.values.patientName);
    if (ids) {
      if (ids.encounterId !== undefined) {
        navigate(`/hub/patient/${ids.patientId}/ioc/${ids.encounterId}/pay`);
      } else {
        navigate(`/hub/patient/${ids.patientId}`);
      }
    } else {
      dispatch(showErrorStatus('Please select a patient first.'));
    }
  };

  const handleSetupPaymentProgram = () => {
    const ids = getPatientAndEncounterIds(formik.values.patientName);
    if (ids) {
      if (ids.encounterId !== undefined) {
        navigate(`/hub/patient/${ids.patientId}/ioc/${ids.encounterId}/program`);
      } else {
        navigate(`/hub/patient/${ids.patientId}`);
      }
    } else {
      dispatch(showErrorStatus('Please select a patient first.'));
    }
  };

  const formatDobAsMDYYYY = (isoDateString: string): string => {
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <main test-id="hub-view">
      <div className="content px-4 py-4">
        <Paper elevation={2} sx={{ p: 4, maxWidth: 900, mx: 'auto' }} test-id="hub-form-paper">
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Payment Hub
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
                {/* Recent Patients Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, '& .MuiInputBase-root': { height: 56 } }}>
                      <TextField
                        select
                        error={formik.touched.patientName && !!formik.errors.patientName}
                        label="Recent Patients"
                        name="patientName"
                        value={formik.values.patientName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePatientChange(e.target.value)}
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.patientName ? formik.errors.patientName : undefined}
                        test-id="hub-patient-name"
                      >
                        <MenuItem value="">
                          <em>Select Patient</em>
                        </MenuItem>
                        {recentPatients
                          .filter((patient) => patient.patientId && patient.patientName)
                          .map((patient) => {
                            const dobFormatted = patient.birthDate
                              ? formatDobAsMDYYYY(patient.birthDate)
                              : '';
                            return (
                              <MenuItem key={patient.patientId} value={patient.patientId.toString()}>
                                {`${patient.patientName}${dobFormatted ? ` - ${dobFormatted}` : ''}`}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                    </Box>
                    <OpenSearchPatient
                        renderSearchPatient={
                            (openOnClick) => {
                              return <Button
                                  type="secondary"
                                  onClick={openOnClick}
                                  disabled={isLoadingPatients}
                                  test-id="hub-search-patient"
                                  style={{ height: 56 }}
                              >
                                Search Patient
                              </Button>
                            }
                        }
                        handleIOCOnClick={(patientId: number, encounterId: number) => {
                          window.open(`${window.location.origin}/hub/patient/${patientId}/ioc/${encounterId}`, '_blank', )
                        }}
                        renderPatientNavigation={(patient: patientSearchResult)=>{
                          return () => {
                            return (
                                <Typography variant="caption" component="div">
                                  {`${patient._source?.firstName} ${patient._source?.lastName}`}
                                </Typography>
                            )
                          }
                        }}
                    />
                    <Button
                      type="secondary"
                      onClick={handleNewPatient}
                      test-id="hub-new-patient"
                      style={{ height: 56 }}
                    >
                      New Patient
                    </Button>
                  </Box>
                </Grid>

                {/* Facility Section - Hidden for now */}
                {/* <Grid item xs={12}>
                  <TextField
                    select
                    error={formik.touched.facility && !!formik.errors.facility}
                    label="Facility"
                    name="facility"
                    value={formik.values.facility}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFacilityChange(e.target.value)}
                    onBlur={formik.handleBlur}
                    helperText={formik.touched.facility ? formik.errors.facility : undefined}
                    test-id="hub-facility"
                  >
                    <MenuItem value="">
                      <em>Select Facility</em>
                    </MenuItem>
                    {facilities.map((facility) => (
                      <MenuItem key={facility.facilityId} value={facility.facilityId.toString()}>
                        {facility.facilityName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid> */}

                {/* IOC Section */}
                <Grid item xs={12}>
                  <TextField
                    select
                    error={formik.touched.ioc && !!formik.errors.ioc}
                    label="IOC"
                    name="ioc"
                    value={formik.values.ioc}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.handleChange(e);
                      const patientId = formik.values.patientName || patientIdParam;
                      const iocId = e.target.value;
                      if (patientId && iocId) {
                        navigate(`/hub/patient/${patientId}/ioc/${iocId}`, { replace: true });
                      }
                    }}
                    onBlur={formik.handleBlur}
                    helperText={formik.touched.ioc ? formik.errors.ioc : undefined}
                    disabled={!formik.values.patientName || isLoadingIocs}
                    test-id="hub-ioc"
                  >
                    <MenuItem value="">
                      <em>Select IOC</em>
                    </MenuItem>
                    {iocDates.map((ioc) => (
                      <MenuItem key={ioc.id} value={ioc.id.toString()}>
                        {ioc.display}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ width: '100%' }}>
                      <Button
                        type="primary"
                        onClick={handleTakePayment}
                        test-id="hub-take-payment"
                        style={{ 
                          width: '100%', 
                          paddingTop: '1rem',
                          paddingBottom: '1rem',
                          fontSize: '2rem',
                          fontWeight: 600
                        }}
                      >
                        Take a Payment
                      </Button>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <Button
                        type="primary"
                        onClick={handleSetupPaymentProgram}
                        test-id="hub-setup-payment-program"
                        style={{ 
                          width: '100%', 
                          paddingTop: '1rem',
                          paddingBottom: '1rem',
                          fontSize: '2rem',
                          fontWeight: 600
                        }}
                      >
                        Setup Payment Program
                      </Button>
                    </Box>
                  </Box>
                </Grid>
            </Grid>
          </form>
        </Paper>
      </div>
    </main>
  );
}

export default HubView;
